import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tasksApi } from '../../services/api';

export const fetchTasks = createAsyncThunk('tasks/list', async (params, { rejectWithValue }) => {
  try {
    const res = await tasksApi.list(params);
    return { items: res.data, pagination: res.pagination };
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const fetchTask = createAsyncThunk('tasks/get', async (id, { rejectWithValue }) => {
  try {
    const res = await tasksApi.get(id);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

// Backend BusinessRuleError / ValidationError 400/422 döndüğünde form'a
// detayı yansıtabilelim diye sadece message değil, code/detail/status'u da
// payload'a koyuyoruz. apiClient.js zaten bu alanları err objesine setliyor.
function normalizeErr(err) {
  return {
    message: err?.message || 'İşlem başarısız',
    code: err?.code,
    status: err?.status,
    detail: err?.detail,
  };
}

export const createTask = createAsyncThunk('tasks/create', async (payload, { rejectWithValue }) => {
  try {
    const res = await tasksApi.create(payload);
    return res.data;
  } catch (err) {
    return rejectWithValue(normalizeErr(err));
  }
});

export const updateTask = createAsyncThunk(
  'tasks/update',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await tasksApi.update(id, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(normalizeErr(err));
    }
  },
);

export const deleteTask = createAsyncThunk('tasks/delete', async (id, { rejectWithValue }) => {
  try {
    await tasksApi.remove(id);
    return Number(id);
  } catch (err) {
    return rejectWithValue(err.message);
  }
});

export const cancelTask = createAsyncThunk(
  'tasks/cancel',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await tasksApi.cancel(id, payload);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

export const recalculateTask = createAsyncThunk(
  'tasks/recalculate',
  async (id, { rejectWithValue }) => {
    try {
      const res = await tasksApi.recalculate(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

/**
 * Bulk push MANUAL-metric scores. Payload must be
 * `{ id, entries: [{user_code, raw_value?, points?}], note? }`.
 * Returns the backend's result envelope: {accepted, updated, skipped, errors, results}.
 */
export const pushManualScores = createAsyncThunk(
  'tasks/manualScores',
  async ({ id, entries, note }, { rejectWithValue }) => {
    try {
      const res = await tasksApi.pushManualScores(id, { entries, note });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  },
);

const initialState = {
  items: [],
  selected: null,
  status: 'idle',
  mutateStatus: 'idle',
  error: null,
  filters: { period_id: null, metric_type: null },
  pagination: { total: 0, page: 1, size: 50 },
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilter(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelected(state) {
      state.selected = null;
    },
    resetStatus(state) {
      state.mutateStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (s) => { s.status = 'loading'; s.error = null; })
      .addCase(fetchTasks.fulfilled, (s, a) => {
        s.status = 'succeeded';
        s.items = a.payload.items;
        if (a.payload.pagination) s.pagination = a.payload.pagination;
      })
      .addCase(fetchTasks.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload; })

      .addCase(fetchTask.fulfilled, (s, a) => { s.selected = a.payload; })

      .addCase(createTask.pending, (s) => { s.mutateStatus = 'loading'; })
      .addCase(createTask.fulfilled, (s, a) => {
        s.mutateStatus = 'succeeded';
        s.items.unshift(a.payload);
      })
      .addCase(createTask.rejected, (s, a) => { s.mutateStatus = 'failed'; s.error = a.payload; })

      .addCase(updateTask.fulfilled, (s, a) => {
        const i = s.items.findIndex((t) => t.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
        if (s.selected?.id === a.payload.id) s.selected = a.payload;
      })

      .addCase(deleteTask.fulfilled, (s, a) => {
        s.items = s.items.filter((t) => t.id !== a.payload);
      })

      .addCase(cancelTask.fulfilled, (s, a) => {
        const i = s.items.findIndex((t) => t.id === a.payload.id);
        if (i >= 0) s.items[i] = a.payload;
      });
  },
});

export const { setFilter, clearSelected, resetStatus } = tasksSlice.actions;
export const selectTasks = (s) => s.tasks.items;
export const selectTasksForPeriod = (periodId) => (s) =>
  s.tasks.items.filter((t) => t.game_period_id === Number(periodId));
export default tasksSlice.reducer;
